import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { SalesRequestResultDTO } from '@app/api/purchase-service-sales';
import { PermissionService } from '@app/core/services/permission.service';
import { parsePositiveIntRouteParam } from '@shared/utils/route-param.util';
import { salesRequestSeverity } from '../../distribution.constants';
import { SalesRequestService } from '../services/sales-request.service';

@Component({
  selector: 'app-sales-request-view',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    ConfirmDialogModule,
    TableModule,
    TagModule,
    ToastModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './sales-request-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesRequestViewComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(SalesRequestService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  readonly statusSeverity = salesRequestSeverity;
  readonly request = signal<SalesRequestResultDTO | null>(null);
  readonly loading = signal(false);

  readonly canApprove = computed(() => this.can('approve'));
  readonly canCreateOrder = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('distribution.sales_order.create');
  });
  readonly isPending = computed(() => this.request()?.status === 'PENDING');
  readonly isApproved = computed(() => this.request()?.status === 'APPROVED');

  private can(action: string): boolean {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      `distribution.sales_request.${action}`,
    );
  }

  ngOnInit(): void {
    const id = parsePositiveIntRouteParam(this.route.snapshot.paramMap.get('id'));
    if (id == null) {
      this.onBack();
      return;
    }
    this.load(id);
  }

  onBack(): void {
    this.router.navigate(['/distribution/sales-request']);
  }

  onApprove(): void {
    this.confirm('distribution.sales_request.confirm_approve', () =>
      this.service.approve(this.request()!.id!),
    );
  }

  onReject(): void {
    this.confirm('distribution.sales_request.confirm_reject', () =>
      this.service.reject(this.request()!.id!),
    );
  }

  /** Hand over to the sales order form, pre-filled from this request. */
  onCreateOrder(): void {
    this.router.navigate(['/distribution/sales-order/add'], {
      queryParams: { salesRequestId: this.request()!.id },
    });
  }

  private confirm(
    messageKey: string,
    action: () => ReturnType<SalesRequestService['approve']>,
  ): void {
    this.confirmationService.confirm({
      message: this.translate.instant(messageKey, {
        series: this.request()?.series,
      }),
      accept: () =>
        action().subscribe({
          next: (updated) => {
            this.request.set(updated);
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('common.messages.success'),
              detail: this.translate.instant('distribution.messages.updated'),
            });
          },
          error: (err) => this.showError(err),
        }),
    });
  }

  private load(id: number): void {
    this.loading.set(true);
    this.service.getById(id).subscribe({
      next: (data) => {
        this.request.set(data);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.showError(err);
      },
    });
  }

  private showError(err: unknown): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('common.messages.error'),
      detail:
        (err as { error?: { message?: string } })?.error?.message ??
        this.translate.instant('distribution.messages.failed'),
    });
  }
}
